import React from "react";
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  capitalize,
  getItemBasePrice,
  getVariantOptionSurchargeForDisplay,
  getVariantTotal,
  sortVariantOptions,
  buildPhotoVariantValue,
} from "../../common/util";
import currency from "currency.js";
import FlavorSelection from "./FlavorSelection";
import PhotoVariantTiles from "./PhotoVariantTiles";
import ClearIcon from "@mui/icons-material/Clear";
import AddCustomOption from "./AddCustomOption";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import CategoryFilterSelect from "../../common/input/CategoryFilterSelect";

export default function ProductVariants(props) {
  const { fields, variant } = props;

  const sortVariants = ([k1, v1], [k2, v2]) => {
    return v2.rank - v1.rank || k2.localeCompare(k1);
  };

  const findOptionByName = (name, arr) => {
    return arr.filter((o) => o.name === name)[0] || "";
  };

  const getOptionName = (fieldKey, option, surcharge) => {
    let str = "";
    if (fieldKey === "size" && option.size) {
      str = option.size;
    } else {
      str = option.name;
    }

    return `${str} ${surcharge && ` (+${surcharge})`}`;
  };

  const getOptionDescription = (fieldKey, option) => {
    if (fieldKey === "size" && option.servings) {
      let arr = [];
      if (option.shape) {
        arr.push(option.shape);
      }
      if (option.layer) {
        arr.push(`${option.layer} Layers`);
      }
      if (option.servings) {
        arr.push(`${option.servings} Servings`);
      }
      return arr.join(" • ");
    } else {
      return option.description;
    }
  };

  const basePrice = props.basePrice || getItemBasePrice(variant);

  return (
    <Box>
      <Grid container spacing={3}>
        {Object.entries(fields)
          .sort(sortVariants)
          .map(([fieldKey, fieldConfig]) => {
            if (fieldConfig.field.type === "text") {
              return (
                <Grid key={fieldKey} item sm={12} xs={12}>
                  <TextField
                    id={fieldKey}
                    value={variant?.[fieldKey]?.name || ""}
                    required={fieldConfig.required}
                    label={fieldConfig.name}
                    name={fieldKey}
                    variant="outlined"
                    inputProps={{ maxLength: fieldConfig.field?.max }}
                    fullWidth
                    multiline={fieldConfig.field?.max > 40}
                    rows={Math.floor(fieldConfig.field?.max / 40)}
                    onChange={(e) => {
                      props.onChange({
                        ...variant,
                        [fieldKey]: { name: e.target.value, rank: fieldConfig.rank || 1 },
                      });
                    }}
                    helperText={fieldConfig.description}
                  />
                </Grid>
              );
            } else if (fieldConfig.field.type === "photo") {
              let surcharge = getVariantOptionSurchargeForDisplay(fieldConfig, basePrice);

              return (
                <Grid key={fieldKey} item sm={12} xs={12}>
                  <Box sx={{ border: "1px solid #CCC", borderRadius: "4px", p: 2 }}>
                    <Typography variant="body1" gutterBottom>
                      {fieldConfig.name || "Upload Photos"} {surcharge && `(+${currency(surcharge)})`}
                      {fieldConfig.required && "*"}
                    </Typography>
                    <PhotoVariantTiles
                      pics={variant?.[fieldKey]?.value || []}
                      max={fieldConfig.field?.max || 1}
                      folder="img/r"
                      onChange={(arr) => {
                        const next = { ...variant };
                        const photoVal = buildPhotoVariantValue(fieldConfig, arr);
                        if (photoVal) {
                          next[fieldKey] = photoVal;
                        } else {
                          delete next[fieldKey];
                        }
                        props.onChange(next);
                      }}
                    />
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1.5 }}>
                      {fieldConfig.description ||
                        "For best results, use high-resolution photos in JPEG or PNG format. We will crop the image to achieve the best possible fit, but we will not alter the design of the uploaded photos."}
                    </Typography>
                  </Box>
                </Grid>
              );
            } else if (
              (fieldKey === "flavor" || fieldKey === "filling") &&
              variant?.size?.value?.settings?.field?.flavor &&
              fields?.flavor?.field
            ) {
              return (
                <Grid key={fieldKey} item sm={12} xs={12}>
                  <FlavorSelection
                    basePrice={basePrice}
                    fieldKey={fieldKey}
                    variant={variant}
                    fields={fields}
                    onChange={(flavor, filling) => {
                      let o = { ...variant };
                      if (flavor) {
                        o.flavor = flavor;
                      } else {
                        delete o.flavor;
                      }
                      if (filling) {
                        o.filling = filling;
                      } else {
                        delete o.filling;
                      }
                      props.onChange(o);
                    }}
                  />
                </Grid>
              );
            } else if (fieldConfig.field.type === "select") {
              const isMultiSelect = +fieldConfig.field?.max > 1;

              if (fieldKey === "size" && !isMultiSelect) {
                // Use CategoryFilterSelect for size field
                let fieldOptions = fieldConfig.field.options;

                // Check if value exists in options. If not, add it
                const valueToCheck = variant?.[fieldKey]?.name
                  ? { ...(variant[fieldKey].value || {}), name: variant[fieldKey].name }
                  : null;
                if (valueToCheck && !fieldOptions.some((o) => o.name === valueToCheck.name)) {
                  fieldOptions.push(valueToCheck);
                }

                return (
                  <Grid key={fieldKey} item sm={12} xs={12}>
                    <CategoryFilterSelect
                      value={valueToCheck}
                      label={capitalize(fieldConfig.name)}
                      required={fieldConfig.required}
                      helperText={fieldConfig.description}
                      options={fieldOptions.sort((o1, o2) => sortVariantOptions(fieldKey, o1, o2))}
                      categoryKey="shape"
                      onChange={(option) => {
                        if (option) {
                          const { override, ...opt } = option;
                          props.onChange({ ...variant, [fieldKey]: { ...opt, value: opt } });
                        } else {
                          let o = { ...variant };
                          delete o[fieldKey];
                          props.onChange(o);
                        }
                      }}
                      customOption={{
                        variantKey: fieldKey,
                        label: `Add Custom ${fieldConfig.name}`,
                        onChange: (o) => {
                          props.onChange({ ...variant, [fieldKey]: { ...o, value: o } });
                        },
                      }}
                      renderValue={(option) => {
                        if (fieldKey === "size" && +option.pricing?.amount > 0) {
                          return `${option.name} (+${currency(option.pricing.amount).format()})`;
                        }
                        let surcharge = getVariantOptionSurchargeForDisplay(option, basePrice);
                        return `${option.name}${surcharge ? ` (+${surcharge})` : ""}`;
                      }}
                      renderOption={(option) => {
                        const sizePriceLabel =
                          fieldKey === "size" && +option.pricing?.amount > 0
                            ? ` (+${currency(option.pricing.amount).format()})`
                            : "";
                        let surcharge = sizePriceLabel
                          ? ""
                          : getVariantOptionSurchargeForDisplay(option, basePrice);
                        let descArr = [];
                        if (option.shape) descArr.push(option.shape);
                        if (option.layer) descArr.push(`${option.layer} Layers`);
                        if (option.servings) descArr.push(`${option.servings} Servings`);

                        return (
                          <Box>
                            <Typography variant="body1">
                              {option.size || option.name}
                              {sizePriceLabel}
                              {surcharge ? ` (+${surcharge})` : ""}
                            </Typography>
                            {descArr.length > 0 && (
                              <Typography variant="body2" color="textSecondary">
                                {descArr.join(" • ")}
                              </Typography>
                            )}
                          </Box>
                        );
                      }}
                    />
                  </Grid>
                );
              } else {
                // check if value exists in options. If not, add it
                let fieldOptions = fieldConfig.field.options;
                let valuesToCheck = [];
                if (variant?.[fieldKey]?.value) {
                  if (Array.isArray(variant?.[fieldKey]?.value)) {
                    valuesToCheck = variant?.[fieldKey]?.value;
                  } else {
                    valuesToCheck = [
                      variant?.[fieldKey]?.value || { name: variant?.[fieldKey]?.name },
                    ];
                  }
                } else if (variant?.[fieldKey]?.name) {
                  valuesToCheck = [{ name: variant[fieldKey].name }];
                }
                for (let value of valuesToCheck) {
                  if (!fieldOptions.some((o) => o.name === value.name)) {
                    fieldOptions.push(value);
                  }
                }

                // convert value to string
                let variantValue = "";
                if (isMultiSelect) {
                  variantValue = [];
                  if (variant?.[fieldKey]?.value) {
                    if (Array.isArray(variant?.[fieldKey]?.value)) {
                      variantValue =
                        variant?.[fieldKey]?.value?.map((o) =>
                          typeof o === "object" ? o.name : o
                        ) || [];
                    } else {
                      variantValue = [variant?.[fieldKey]?.value?.name];
                    }
                  }
                } else {
                  variantValue =
                    variant?.[fieldKey]?.value?.name || variant?.[fieldKey]?.name || "";
                }

                return (
                  <Grid key={fieldKey} item sm={12} xs={12}>
                    <FormControl variant="outlined" fullWidth required={fieldConfig.required}>
                      <InputLabel id={`${fieldKey}-label`}>
                        {capitalize(fieldConfig.name)}
                      </InputLabel>
                      <Select
                        value={variantValue}
                        required={fieldConfig.required}
                        labelId={`${fieldKey}-label`}
                        label={capitalize(fieldKey)}
                        id={fieldKey}
                        multiple={isMultiSelect}
                        renderValue={(selected) => {
                          if (isMultiSelect) {
                            return (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {selected?.map((o, idx) => {
                                  const variantValueObj = findOptionByName(o, fieldOptions);
                                  let surcharge = getVariantOptionSurchargeForDisplay(
                                    variantValueObj,
                                    basePrice
                                  );

                                  return (
                                    <Chip
                                      key={idx}
                                      label={`${variantValueObj.name} ${surcharge && ` (+${surcharge})`}`}
                                    />
                                  );
                                })}
                              </Box>
                            );
                          } else {
                            const variantValueObj =
                              findOptionByName(selected, fieldConfig.field.options) ||
                              variant?.[fieldKey]?.value;

                            let surcharge = getVariantOptionSurchargeForDisplay(
                              variantValueObj,
                              basePrice
                            );
                            return `${variantValueObj.name} ${surcharge && ` (+${surcharge})`}`;
                          }
                        }}
                        onChange={(e) => {
                          if (Array.isArray(e.target.value)) {
                            let variantValueArr = e.target.value.map((o) =>
                              findOptionByName(o, fieldConfig.field.options)
                            );

                            props.onChange({
                              ...variant,
                              [fieldKey]: {
                                name: variantValueArr.map((o) => o.name).join(", "),
                                rank: fieldConfig.rank || 1,
                                pricing: { amount: getVariantTotal(variantValueArr, basePrice) },
                                value: variantValueArr,
                              },
                            });
                          } else {
                            const variantValueObj = findOptionByName(
                              e.target.value,
                              fieldConfig.field.options
                            );
                            const { override, ...opt } = variantValueObj;
                            props.onChange({ ...variant, [fieldKey]: { ...opt, value: opt } });
                          }
                        }}
                        IconComponent={() => null}
                        endAdornment={
                          variantValue && Object.keys(variantValue).length > 0 ? (
                            <InputAdornment sx={{ position: "absolute", right: 4 }} position="end">
                              <IconButton
                                onClick={() => {
                                  let o = { ...variant };
                                  delete o[fieldKey];
                                  props.onChange(o);
                                }}
                              >
                                <ClearIcon></ClearIcon>
                              </IconButton>
                            </InputAdornment>
                          ) : isMultiSelect ? null : (
                            <InputAdornment sx={{ position: "absolute", right: 4 }} position="end">
                              <AddCustomOption
                                variantKey={fieldKey}
                                label={`Add Custom ${fieldConfig.name}`}
                                onChange={(o) => {
                                  props.onChange({ ...variant, [fieldKey]: { ...o, value: o } });
                                }}
                              >
                                <IconButton>
                                  <PlaylistAddIcon />
                                </IconButton>
                              </AddCustomOption>
                            </InputAdornment>
                          )
                        }
                      >
                        {fieldOptions
                          .sort((o1, o2) => sortVariantOptions(fieldKey, o1, o2))
                          .map((option, idx) => {
                            if (isMultiSelect) {
                              let isSelected = false;
                              if (
                                valuesToCheck.filter(
                                  (o) => (typeof o === "object" ? o.name : o) === option.name
                                )?.length > 0
                              ) {
                                isSelected = true;
                              }
                              let surcharge = getVariantOptionSurchargeForDisplay(
                                option,
                                basePrice
                              );

                              return (
                                <MenuItem
                                  key={idx}
                                  value={option.name}
                                  style={{ whiteSpace: "normal" }}
                                  disabled={
                                    !isSelected &&
                                    variant?.[fieldKey]?.value.length >= +fieldConfig.field?.max
                                  }
                                >
                                  <Checkbox checked={isSelected} />
                                  <Box>
                                    <Typography variant="body1">
                                      {getOptionName(fieldKey, option, surcharge)}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                      {getOptionDescription(fieldKey, option)}
                                    </Typography>
                                  </Box>
                                </MenuItem>
                              );
                            } else {
                              let surcharge = getVariantOptionSurchargeForDisplay(
                                option,
                                basePrice
                              );

                              return (
                                <MenuItem
                                  key={idx}
                                  value={option.name}
                                  style={{ whiteSpace: "normal" }}
                                >
                                  <Box>
                                    <Typography variant="body1">
                                      {getOptionName(fieldKey, option, surcharge)}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                      {getOptionDescription(fieldKey, option)}
                                    </Typography>
                                  </Box>
                                </MenuItem>
                              );
                            }
                          })}
                      </Select>
                      <FormHelperText>{fieldConfig.description}</FormHelperText>
                    </FormControl>
                  </Grid>
                );
              }
            }
          })}
      </Grid>
    </Box>
  );
}
